<template>
  <b-row align-v="stretch" class="h-100">
    <b-col align-self="stretch" cols="12" class="pr-0">
      <b-row v-for="item in items" :key="item.id" class="d-flex align-items-center">
        <b-col ref="text__lu-name" cols="3">
          <h6 v-if="item.learning_unit_name" class="text__lu-name font-weight-bold">
            {{ item.learning_unit_name }}
          </h6>
        </b-col>
        <b-col ref="text__lu-owner" cols="4" class="d-flex text__lu-owner">
          <base-avatar
            ref="text__lu-owner-short-name"
            :name="`${item.firstname} ${item.lastname}`"
            :size="50"
            class="avatar-icon-wrapper avatar-initials avatar-icon-md mr-3"
          />

          <div class="word-break">
            <h6 ref="text__lu-owner-username" class="text__lu-owner-name">
              {{ rewriteUserName(item.firstname, item.lastname) }}
            </h6>
            <h6 ref="text__lu-owner-email" class="text__lu-owner-email text-black-50">
              {{ item.email }}
            </h6>
          </div>
        </b-col>
        <b-col ref="text__lu-submitted-date" cols="3">
          <div
            v-if="!item.is_published && !item.required_review"
            class="d-flex align-items-baseline text__lu-created-date"
          >
            <font-awesome-icon
              :icon="{ prefix: 'fas', iconName: 'circle' }"
              class="text-primary mr-2"
              style="font-size: 0.65rem; color: #f4772e"
            />

            <div>
              <div v-if="item.latest_request_changes_at" class="text__lu-request-changes-at">
                {{ formatRequestChanges(item) }}
              </div>

              <div class="text__lu-created-at">{{ formatSubmittedDateDraft(item) }}</div>
            </div>
          </div>

          <div
            v-if="!item.is_published && item.required_review"
            class="d-flex align-items-baseline text__lu-submitted-date"
          >
            <font-awesome-icon
              :icon="{ prefix: 'fas', iconName: 'circle' }"
              class="text-warning mr-2"
              style="font-size: 0.65rem; color: #f4772e"
            />

            <div>
              <div v-if="item.latest_request_changes_at" class="text__lu-request-changes-at">
                {{ formatRequestChanges(item) }}
              </div>

              <div class="text__lu-submitted-review-at">{{ formatSubmittedDateReview(item) }}</div>
            </div>
          </div>

          <div v-if="item.is_published" class="d-flex align-items-baseline text__lu-published-date">
            <font-awesome-icon
              :icon="{ prefix: 'fas', iconName: 'circle' }"
              class="text-success mr-2"
              style="font-size: 0.65rem; color: #f4772e"
            />
            {{ formatSubmittedDatePublished(item) }}
          </div>
        </b-col>
        <b-col ref="button__edit-lu" cols="1">
          <!-- The reviewer's edit flow lives in the catalog (Laravel) app. Learning unit
               ids are identical in both databases, so the id maps across directly; the
               catalog resolves the locale, as it does for /login. -->
          <a :href="catalogReviewUrl(item.id)" target="_blank" rel="noopener">
            <b-button variant="outline-primary" class="button__edit-lu"> Edit </b-button>
          </a>
        </b-col>
        <b-col ref="button__copy-lu-link" cols="1" class="d-flex justify-content-center">
          <router-link
            :to="{
              name: 'learning-unit/all-information',
              params: { learningUnitId: item.id }
            }"
            target="_blank"
          >
            <font-awesome-icon
              :icon="{ prefix: 'fas', iconName: 'external-link-alt' }"
              class="button__copy-lu-link text-primary cursor-pointer"
              size="lg"
            />
          </router-link>
        </b-col>

        <b-col cols="12">
          <hr />
        </b-col>
      </b-row>
    </b-col>

    <b-col align-self="end" cols="12">
      <b-row align-h="center" class="mt-3">
        <b-pagination-nav
          v-model.number="$currentPage"
          :link-gen="linkGenFn"
          :number-of-pages="numberOfPages > 0 ? numberOfPages : 1"
          align="center"
          use-router
        ></b-pagination-nav>
      </b-row>
    </b-col>
  </b-row>
</template>

<script>
import { datetimeMixin } from '@/mixins/datetimeMixin';
import BaseAvatar from '../base-components/BaseAvatar.vue';

export default {
  components: {
    BaseAvatar
  },
  mixins: [datetimeMixin],

  props: {
    items: {
      type: Array,
      default: () => []
    },
    currentPage: {
      type: Number,
      default: 1
    },
    numberOfPages: {
      type: Number,
      default: 1
    },
    linkGenFn: {
      type: Function,
      default: (pageNumber) => {
        return pageNumber === 1 ? '?' : `?page=${pageNumber}`;
      }
    }
  },

  computed: {
    $currentPage: {
      get() {
        return this.currentPage;
      },

      set(val) {
        return this.$emit('update:currentPage', val);
      }
    }
  },

  methods: {
    formatSubmittedDateReview(item) {
      const text = item.submitted_review_at
        ? this.formatMessageDatetime(item.submitted_review_at)
        : '-';

      return `Submitted: ${text}`;
    },

    formatSubmittedDateDraft(item) {
      const text = item.created_at ? this.formatMessageDatetime(item.created_at) : '-';

      return `Created: ${text}`;
    },

    formatRequestChanges(item) {
      const prefix = item.submitted_review_at ? 'Submitted' : 'Created';

      // const text = item.latest_request_changes_at
      //   ? this.formatMessageDatetime(item.latest_request_changes_at)
      //   : "-";

      return `${prefix} Requested changes`;
    },

    formatSubmittedDatePublished(item) {
      const text = item.published_at ? this.formatMessageDatetime(item.published_at) : '-';

      return `Published: ${text}`;
    },

    edit() {},

    catalogReviewUrl(learningUnitId) {
      return new URL(
        `/design-planning/${learningUnitId}`,
        process.env.VUE_APP_CATALOG_APP_URL
      ).toString();
    },

    rewriteUserName(firstname, lastname) {
      return `${firstname} ${lastname}`;
    }
  }
};
</script>

<style lang="scss" scoped>
.cursor-pointer {
  cursor: pointer;
}

.word-break {
  word-break: break-word;
}
</style>
